/**
 * useKatalogBarState - Shared state bridge between YourKatalogPage and KatalogBarRow
 * 
 * Uses custom DOM events to sync state without context providers.
 * This allows the sticky header to control the same state as the page.
 */
import { useEffect, useCallback, useState } from 'react';

export type KatalogTabValue = 'katalog' | 'events' | 'portfolio';
export type ViewMode = 'grid' | 'list' | 'reel';

export interface KatalogBarState {
  activeTab: KatalogTabValue;
  searchQuery: string;
  viewMode: ViewMode;
  filterActive: boolean;
  avatarUrl?: string;
  name: string;
}

const KATALOG_STATE_KEY = 'katalog-bar-state';
const KATALOG_STATE_CHANGE_EVENT = 'katalog-bar-state-change';
const KATALOG_ACTION_EVENT = 'katalog-bar-action';

type ActionType = 
  | { type: 'setActiveTab'; value: KatalogTabValue }
  | { type: 'setSearchQuery'; value: string }
  | { type: 'setViewMode'; value: ViewMode }
  | { type: 'toggleFilter' };

/**
 * Hook for the page (YourKatalogPage) - provides state and listens for actions
 */
export function useKatalogBarStatePage(
  initialState: Partial<KatalogBarState> = {}
) {
  const [state, setState] = useState<KatalogBarState>({
    activeTab: 'katalog',
    searchQuery: '',
    viewMode: 'grid',
    filterActive: false,
    name: 'Your Katalag',
    ...initialState,
  });

  // Broadcast state to window for header to read
  useEffect(() => {
    (window as any)[KATALOG_STATE_KEY] = state;
    window.dispatchEvent(new CustomEvent(KATALOG_STATE_CHANGE_EVENT, { detail: state }));
  }, [state]);

  // Listen for actions from header
  useEffect(() => {
    const handleAction = (e: CustomEvent<ActionType>) => {
      const action = e.detail;
      switch (action.type) {
        case 'setActiveTab':
          setState(s => ({ ...s, activeTab: action.value }));
          break;
        case 'setSearchQuery':
          setState(s => ({ ...s, searchQuery: action.value }));
          break;
        case 'setViewMode':
          setState(s => ({ ...s, viewMode: action.value }));
          break;
        case 'toggleFilter':
          setState(s => ({ ...s, filterActive: !s.filterActive }));
          break;
      }
    };

    window.addEventListener(KATALOG_ACTION_EVENT, handleAction as EventListener);
    return () => {
      window.removeEventListener(KATALOG_ACTION_EVENT, handleAction as EventListener);
      delete (window as any)[KATALOG_STATE_KEY];
    };
  }, []);

  // Setters that update local state (page can use these directly)
  const setActiveTab = useCallback((tab: KatalogTabValue) => {
    setState(s => ({ ...s, activeTab: tab }));
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setState(s => ({ ...s, searchQuery: query }));
  }, []);

  const setViewMode = useCallback((mode: ViewMode) => {
    setState(s => ({ ...s, viewMode: mode }));
  }, []);

  const toggleFilter = useCallback(() => {
    setState(s => ({ ...s, filterActive: !s.filterActive }));
  }, []);

  const updateProfile = useCallback((profile: { name?: string; avatarUrl?: string }) => {
    setState(s => ({ ...s, ...profile }));
  }, []);

  return {
    state,
    setActiveTab,
    setSearchQuery,
    setViewMode,
    toggleFilter,
    updateProfile,
  };
}

/**
 * Hook for the header (KatalogBarRow) - reads state and dispatches actions
 */
export function useKatalogBarStateHeader() {
  const [state, setState] = useState<KatalogBarState | null>(() => {
    return (window as any)[KATALOG_STATE_KEY] || null;
  });

  // Listen for state changes from page
  useEffect(() => {
    const handleChange = (e: CustomEvent<KatalogBarState>) => {
      setState(e.detail);
    };

    // Get initial state
    setState((window as any)[KATALOG_STATE_KEY] || null);

    window.addEventListener(KATALOG_STATE_CHANGE_EVENT, handleChange as EventListener);
    return () => {
      window.removeEventListener(KATALOG_STATE_CHANGE_EVENT, handleChange as EventListener);
    };
  }, []);

  // Action dispatchers
  const dispatchAction = useCallback((action: ActionType) => {
    window.dispatchEvent(new CustomEvent(KATALOG_ACTION_EVENT, { detail: action }));
  }, []);

  const setActiveTab = useCallback((tab: KatalogTabValue) => {
    dispatchAction({ type: 'setActiveTab', value: tab });
  }, [dispatchAction]);

  const setSearchQuery = useCallback((query: string) => {
    dispatchAction({ type: 'setSearchQuery', value: query });
  }, [dispatchAction]);

  const setViewMode = useCallback((mode: ViewMode) => {
    dispatchAction({ type: 'setViewMode', value: mode });
  }, [dispatchAction]);

  const toggleFilter = useCallback(() => {
    dispatchAction({ type: 'toggleFilter' });
  }, [dispatchAction]);

  return {
    state,
    setActiveTab,
    setSearchQuery,
    setViewMode,
    toggleFilter,
  };
}
